import React, { useMemo } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { CheckCircle, AlertTriangle, RefreshCw, ClipboardCheck} from 'lucide-react';
import PageHeader from '../../../layouts/PageHeader';
import TodoGroup from './TodoGroup';
import { useTodos } from './TodoContext';
import { DOMAIN_TITLE } from './domainTitles';

/**
 * ToDo page — renders the admin aggregator output as one section per
 * domain. V1 domains: expenses, time_reports, tax_reporting. Adding a
 * new domain backend-side automatically shows up here without frontend
 * changes (the domain key drives grouping + heading).
 */

const TodoPage = () => {
  const { loading, groups, errors, forbidden, refresh, generatedAt } = useTodos();

  const byDomain = useMemo(() => {
    const map = {};
    for (const g of groups) {
      (map[g.domain] = map[g.domain] || []).push(g);
    }
    return map;
  }, [groups]);

  const domains = Object.keys(byDomain);

  return (
    <>
      <PageHeader
        icon={ClipboardCheck}
        title="Att göra"
        subtitle="Allt som väntar på dig — samlat på ett ställe."
      />

      <div className="d-flex align-items-center justify-content-between mb-3">
        <div style={{ fontSize: 13, color: '#64748b' }}>
          {generatedAt && <>Uppdaterad {new Date(generatedAt).toLocaleTimeString('sv-SE')}</>}
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={refresh}
          disabled={loading}
        >
          <RefreshCw size={14} className="me-1" /> Uppdatera
        </button>
      </div>

      {forbidden && (
        <Alert variant="info">
          Att göra-listan är tillgänglig för administratörer.
        </Alert>
      )}

      {errors && errors.length > 0 && (
        <Alert variant="warning" style={{ fontSize: 13 }}>
          <AlertTriangle size={14} className="me-1" />
          Vissa uppgifter kunde inte laddas: {errors.map((e) => e.contributor).join(', ')}.
        </Alert>
      )}

      {loading && domains.length === 0 && (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      )}

      {!loading && !forbidden && domains.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '64px 16px', color: '#64748b',
          background: '#f8fafc', border: '1px dashed #e2e8f0', borderRadius: 12,
        }}>
          <CheckCircle size={40} style={{ color: '#10b981', marginBottom: 12 }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>Inget att göra just nu</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Ingenting väntar på din handläggning.</div>
        </div>
      )}

      {domains.map((domain) => (
        <div key={domain} style={{ marginBottom: 20 }}>
          <div style={{
            fontSize: 11, fontWeight: 600, color: '#64748b',
            textTransform: 'uppercase', letterSpacing: 0.04, marginBottom: 8,
          }}>
            {DOMAIN_TITLE[domain] || domain}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {byDomain[domain].map((g) => (
              <TodoGroup key={g.id} group={g} />
            ))}
          </div>
        </div>
      ))}
    </>
  );
};

export default TodoPage;
